import { useMemo } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DraftNodeEnvelope, GetProcessDraftResponse } from '@bliks-io/contracts';
import { processDraftKeys } from '../query-keys';
import { applyDraftCache } from '../mutations/helpers/draftCacheHelpers';
import { findDraftNode, reconcileEnvelope, resolveNodeRef } from './core';
import {
  DraftNodeNotFoundError,
  type DraftGraphNode,
  type DraftNodeKind,
  type DraftNodeRef
} from './types';

/**
 * Stateless handle over the draft-detail cache entry for one process: the
 * pure core functions bound to "whatever the cache holds right now". Safe to
 * keep across renders — every call re-reads the cache.
 */
export interface DraftGraph {
  /** Current draft-detail cache entry, if loaded. */
  snapshot: () => GetProcessDraftResponse | undefined;
  /** {@link findDraftNode} against the current snapshot. */
  findNode: (nodeId: string) => DraftGraphNode | undefined;
  /**
   * {@link resolveNodeRef} against the current snapshot. Also throws
   * {@link DraftNodeNotFoundError} when the draft isn't in the cache at all.
   */
  resolveNodeRef: (
    nodeId: string,
    expectedKind?: DraftNodeKind | readonly DraftNodeKind[]
  ) => DraftNodeRef;
  /** Write a mutation-response envelope into the cache via {@link reconcileEnvelope}. */
  applyEnvelope: (envelope: DraftNodeEnvelope) => void;
}

export function createDraftGraph(queryClient: QueryClient, processId: string): DraftGraph {
  const snapshot = () =>
    queryClient.getQueryData<GetProcessDraftResponse>(processDraftKeys.detail(processId));
  return {
    snapshot,
    findNode: (nodeId) => {
      const draft = snapshot();
      return draft ? findDraftNode(draft, nodeId) : undefined;
    },
    resolveNodeRef: (nodeId, expectedKind) => {
      const draft = snapshot();
      if (!draft) throw new DraftNodeNotFoundError(nodeId, expectedKind);
      return resolveNodeRef(draft, nodeId, expectedKind);
    },
    applyEnvelope: (envelope) => {
      applyDraftCache(queryClient, processId, (prev) => reconcileEnvelope(prev, envelope));
    }
  };
}

export function useDraftGraph(processId: string): DraftGraph {
  const queryClient = useQueryClient();
  return useMemo(() => createDraftGraph(queryClient, processId), [queryClient, processId]);
}
